import React from "react";
import { useFormik } from "formik";

import { ITemp } from "../users-table";
import endpoints from "../../../../api/endpoints";
import { swrKeyMatchesAllpanelDownlineList } from "../../../../api/swr-revalidate";
import usePutApi from "../../../../hooks/use-put-api";
import { showErrorToast, showSuccessToast } from "src/utils/toast";
import { useSyncGlobalPageLoader } from "../../../../contexts/global-page-loader-context";
import StatusSchema, { IStatusSchema } from "./schema/status.schema";

interface Props {
  openModal: boolean;
  toggleModal: () => void;
  rowData: ITemp;
  parentUserId: string;
}

const ChangeStatusModal: React.FC<Props> = ({ openModal, toggleModal, rowData, parentUserId }) => {
  const { putData, loading: putLoading } = usePutApi(endpoints.member.operation(rowData.id), [
    endpoints.member.getMember(parentUserId),
    swrKeyMatchesAllpanelDownlineList(parentUserId),
  ]);

  useSyncGlobalPageLoader(putLoading);

  const { values, handleChange, handleBlur, handleSubmit, resetForm, errors, setFieldValue } =
    useFormik<IStatusSchema>({
      initialValues: {
        is_active: rowData.user_status,
        is_bet_lock: rowData.bet_status,
        transaction_password: "",
        member_operation_type: "change-status",
      },
      validationSchema: StatusSchema,
      onSubmit: (formValues) => {
        handleChangeStatusSubmit(formValues);
      },
    });

  const handleChangeStatusSubmit = async (submitData: IStatusSchema) => {
    try {
      await putData(submitData);
      showSuccessToast("Status Changed Successful");
      toggleModal();
      resetForm();
    } catch (error: any) {
      const errorMessage = error?.data?.status_message;
      showErrorToast(errorMessage || "Someting Went Wrong");
      toggleModal();
      resetForm();
    }
  };

  if (!openModal) return null;

  return (
    <>
    {/* style={{ position: "absolute", zIndex: 1040 }} */}
      <div
        role="dialog"
        className="modal fade show"
        aria-modal="true"
        style={{ display: "block" }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            toggleModal();
          }
        }}
      >
        <div className="modal-dialog modal-md modal-dialog-scrollable">
          <span />
          <div className="modal-content" style={{ marginTop: "60px" }}>
            <header className="modal-header">
              <h4 className="modal-title">Change Status</h4>
              <button type="button" data-dismiss="modal" className="close text-white" onClick={toggleModal}>
                &times;
              </button>
            </header>
            <div className="modal-body">
              <div className="m-t-20">
                <div className="row">
                  <div className="col-6 text-left">
                    <p className="text-warning">{rowData.username}</p>
                  </div>
                </div>
              </div>

              <form method="post" onSubmit={handleSubmit}>
                <div className="form-group row">
                  <div className="col-6 text-center">
                    <span className="col-form-label d-block">User Active</span>
                    <div
                      className={`vue-js-switch${values.is_active ? " toggled" : ""}`}
                      role="switch"
                      aria-checked={values.is_active}
                      tabIndex={0}
                      onClick={() => setFieldValue("is_active", !values.is_active)}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          setFieldValue("is_active", !values.is_active);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        className="v-switch-input"
                        checked={values.is_active}
                        onChange={() => {}}
                      />
                      <div
                        className="v-switch-core"
                        style={{
                          width: "50px",
                          height: "22px",
                          backgroundColor: values.is_active ? "rgb(0, 136, 204)" : "rgb(189, 189, 189)",
                          borderRadius: "11px",
                        }}
                      >
                        <div
                          className="v-switch-button"
                          style={{
                            width: "16px",
                            height: "16px",
                            transition: "transform 300ms",
                            transform: values.is_active
                              ? "translate3d(31px, 3px, 0px)"
                              : "translate3d(3px, 3px, 0px)",
                          }}
                        />
                      </div>
                      <span className={`v-switch-label ${values.is_active ? "v-left" : "v-right"}`} style={{ lineHeight: "22px" }}>
                        {values.is_active ? "on" : "off"}
                      </span>
                    </div>
                  </div>

                  <div className="col-6 text-center">
                    <span className="col-form-label d-block">Bet Active</span>
                    <div
                      className={`vue-js-switch${values.is_bet_lock ? " toggled" : ""}`}
                      role="switch"
                      aria-checked={values.is_bet_lock}
                      tabIndex={0}
                      onClick={() => setFieldValue("is_bet_lock", !values.is_bet_lock)}
                      onKeyDown={(e) => {
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          setFieldValue("is_bet_lock", !values.is_bet_lock);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        className="v-switch-input"
                        checked={values.is_bet_lock}
                        onChange={() => {}}
                      />
                      <div
                        className="v-switch-core"
                        style={{
                          width: "50px",
                          height: "22px",
                          backgroundColor: values.is_bet_lock ? "rgb(0, 136, 204)" : "rgb(189, 189, 189)",
                          borderRadius: "11px",
                        }}
                      >
                        <div
                          className="v-switch-button"
                          style={{
                            width: "16px",
                            height: "16px",
                            transition: "transform 300ms",
                            transform: values.is_bet_lock
                              ? "translate3d(31px, 3px, 0px)"
                              : "translate3d(3px, 3px, 0px)",
                          }}
                        />
                      </div>
                      <span className={`v-switch-label ${values.is_bet_lock ? "v-left" : "v-right"}`} style={{ lineHeight: "22px" }}>
                        {values.is_bet_lock ? "on" : "off"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-form-label col-4" htmlFor="statusTransactionPassword">Transaction Password</label>
                  <div className="col-8 form-group-feedback-right pl-0">
                    <input
                      id="statusTransactionPassword"
                      type="password"
                      name="transaction_password"
                      className={`form-control ${errors.transaction_password ? "is-invalid" : ""}`}
                      value={values.transaction_password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                </div>

                <div className="form-group row">
                  <div className="col-12 text-right">
                    <button type="button" className="btn btn-back" onClick={toggleModal}>
                      <i className="fas fa-undo mr-1" />
                      Back
                    </button>{" "}
                    <button type="submit" className="btn btn-primary">
                      Submit
                      <i className="fas fa-sign-in-alt ml-1" />
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
          <span />
        </div>
      </div>
      <div
        className="modal-backdrop"
        role="button"
        tabIndex={0}
        aria-label="Close change status modal"
        onClick={toggleModal}
      />
    </>
  );
};

export default ChangeStatusModal;
